import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import axios from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import { sortAlphabetically } from "../../utils/sortAlphabetically";

export default function AddUserForm({ open, onClose, onSubmit, onAddUser }) {
  const [name, setName] = useState("");
  // const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [parentGroup, setParentGroup] = useState("");
  const [webAccess, setWebAccess] = useState("No web Access");
  const [selectedAppId, setSelectedAppId] = useState("");
  const [selectedNotificationGroup, setSelectedNotificationGroup] =
    useState("");

  const [groups, setGroups] = useState([]);
  const [notificationGroups, setNotificationGroups] = useState([]);
  const [apps, setApps] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);

  const [loadingGroups, setLoadingGroups] = useState(false);

  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({
    name: false,
    email: false,
    loginPassword: false,
    // userName: false,
    phone: false,
  });

  // Fetch parent groups
  useEffect(() => {
    if (!open) return;
    const fetchGroups = async () => {
      try {
        setLoadingGroups(true);
        const res = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/groups?type=geographical`,
          {
            params: { companyId: process.env.REACT_APP_COMPANY_ID },
          }
        );
        const sortedData = sortAlphabetically(
          Array.isArray(res.data) ? res.data : [],
          "name"
        );
        setGroups(sortedData);
      } catch (err) {
        console.error("❌ Error fetching groups:", err);
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchGroups();
  }, [open]);

  // Fetch notification groups
  useEffect(() => {
    if (!open) return;
    const fetchNotificationGroups = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/notificationGroups?companyId=${process.env.REACT_APP_COMPANY_ID}`
        );
        const sortedData = sortAlphabetically(
          Array.isArray(res.data) ? res.data : [],
          "name"
        );
        setNotificationGroups(sortedData);
      } catch (err) {
        console.error("❌ Error fetching notification groups:", err);
      }
    };
    fetchNotificationGroups();
  }, [open]);
  // ✅ Fetch available applications (for web access)
  useEffect(() => {
    if (!open) return;
    const fetchApps = async () => {
      try {
        setLoadingApps(true);
        const res = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/applications`,
          { params: { companyId: process.env.REACT_APP_COMPANY_ID } }
        );
        setApps(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("❌ Error fetching applications:", err);
      } finally {
        setLoadingApps(false);
      }
    };
    fetchApps();
  }, [open]);
  // Email validator
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Us phone number formatter
  const formatUsPhone = (value) => {
    const cleaned = value.replace(/\D/g, "");

    if (cleaned.length === 0) return "";
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6)
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
      6,
      10
    )}`;
  };

  // Us phone validator (10 digits)
  const isValidUsPhone = (phone) => {
    const cleaned = phone.replace(/\D/g, "");
    return cleaned.length === 10;
  };

  // Phone change handler
  const handlePhoneChange = (e) => {
    const input = e.target.value;
    const cleaned = input.replace(/\D/g, "");

    if (cleaned.length <= 10) {
      const formatted = formatUsPhone(cleaned);
      setPhone(formatted);

      if (errors.phone && isValidUsPhone(formatted)) {
        setErrors((prev) => ({ ...prev, phone: false }));
      }
    }
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {
      name: name.trim() === "",
      email: email.trim() === "" || !isValidEmail(email),
      loginPassword: loginPassword.trim() === "",
      // userName: userName.trim() === "",
      phone: phone.trim() === "" || !isValidUsPhone(phone),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  // Submit handler
  const handleAddUser = async () => {
    setLoading(true);
    // Validate form before submission
    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      // Remove formatting for API submission (send only digits)
      const cleanedPhone = phone.replace(/\D/g, "");

      const payload = {
        companyId: process.env.REACT_APP_COMPANY_ID,
        username: email,
        name,
        email,
        password: loginPassword,
        phone: cleanedPhone,
        groupId: parentGroup,
        notificationGroupId: selectedNotificationGroup || undefined,
        appId: selectedAppId || undefined, // ✅ add this line
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/users/`,
        payload
      );

      toast.success("✅ User created successfully!");
      onSubmit?.(response.data);

      onAddUser?.();
      // Reset form
      setName("");
      // setUserName("");
      setEmail("");
      setLoginPassword("");
      setPhone("");
      setParentGroup("");
      setSelectedAppId("");
      setWebAccess("No web Access");
      setSelectedNotificationGroup("");
      setErrors({});
      onClose();
    } catch (error) {
      const rawMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data ||
        error.message;

      let errorMessage = "Failed to create user";

      if (typeof rawMessage === "string") {
        const lowerMsg = rawMessage.toLowerCase();

        if (lowerMsg.includes("email")) {
          errorMessage = "This email address is already registered.";
        } else if (
          lowerMsg.includes("username") &&
          lowerMsg.includes("already exists with")
        ) {
          errorMessage = "This username is already taken.";
        } else if (
          lowerMsg.includes("phone") &&
          lowerMsg.includes("already exists with")
        ) {
          errorMessage = "This phone number is already in use.";
        } else if (lowerMsg.includes("phone")) {
          errorMessage = "Please enter a valid 10-digit phone number.";
        } else if (rawMessage.includes("UserService create failed:")) {
          errorMessage = rawMessage
            .split("UserService create failed:")[1]
            .trim();
        } else {
          errorMessage = rawMessage;
        }
      }

      toast.error(errorMessage);
      console.error("❌ Error creating user:", rawMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New User</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name && e.target.value.trim()) {
                setErrors((prev) => ({ ...prev, name: false }));
              }
            }}
            fullWidth
            required
            error={errors.name}
            helperText={errors.name ? "Name is required" : ""}
          />

          {/* <TextField
            label="Username"
            value={userName}
            onChange={(e) => {
              setUserName(e.target.value);
              if (errors.userName && e.target.value.trim()) {
                setErrors((prev) => ({ ...prev, userName: false }));
              }
            }}
            fullWidth
            required
            error={errors.userName}
            helperText={errors.userName ? "Username is required" : ""}
          /> */}

          <TextField
            label="Email / Username"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors.email && isValidEmail(e.target.value)) {
                setErrors((prev) => ({ ...prev, email: false }));
              }
            }}
            fullWidth
            required
            error={errors.email}
            helperText={
              errors.email
                ? email.trim() === ""
                  ? "Email is required"
                  : "Enter a valid email"
                : ""
            }
          />

          <TextField
            label="Login Password"
            type="password"
            value={loginPassword}
            onChange={(e) => {
              setLoginPassword(e.target.value);
              if (errors.loginPassword && e.target.value.trim()) {
                setErrors((prev) => ({ ...prev, loginPassword: false }));
              }
            }}
            fullWidth
            required
            error={errors.loginPassword}
            helperText={errors.loginPassword ? "Password is required" : ""}
          />

          <TextField
            label="Phone"
            value={phone}
            onChange={handlePhoneChange}
            placeholder="(123) 456-7890"
            fullWidth
            required
            error={errors.phone}
            helperText={
              errors.phone
                ? phone.trim() === ""
                  ? "Phone number is required"
                  : "Enter a valid 10-digit phone number"
                : "Format: (123) 456-7890"
            }
          />

          <TextField
            select
            label="Parent Group"
            value={parentGroup}
            onChange={(e) => setParentGroup(e.target.value)}
            fullWidth
            required
            disabled={loadingGroups}
          >
            {Array.isArray(groups) && groups.length > 0 ? (
              groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                {loadingGroups ? "Loading groups..." : "No groups available"}
              </MenuItem>
            )}
          </TextField>

          <TextField
            select
            label="Web Access (Application)"
            value={selectedAppId}
            onChange={(e) => setSelectedAppId(e.target.value)}
            fullWidth
            disabled={loadingApps}
          >
            <MenuItem value="">No Web Access</MenuItem>
            {Array.isArray(apps) && apps.length > 0 ? (
              apps.map((app) => (
                <MenuItem key={app.id} value={app.id}>
                  {app.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                {loadingApps ? "Loading apps..." : "No apps available"}
              </MenuItem>
            )}
          </TextField>

          <TextField
            select
            label="Notification Group"
            value={selectedNotificationGroup}
            onChange={(e) => setSelectedNotificationGroup(e.target.value)}
            fullWidth
          >
            <MenuItem value="">None</MenuItem>

            {Array.isArray(notificationGroups) &&
              notificationGroups.length > 0 ? (
              notificationGroups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                {loadingGroups ? "Loading..." : "No groups available"}
              </MenuItem>
            )}
          </TextField>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          sx={{
            borderRadius: 0, // ✅ remove rounding
          }}
          onClick={onClose}
          color="error"
        >
          Cancel
        </Button>
        <Button
          onClick={handleAddUser}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            loadingGroups ||
            !name.trim() ||
            !email.trim() ||
            // !userName.trim() ||
            !loginPassword.trim() ||
            !phone.trim() ||
            !parentGroup
          }
          startIcon={
            loading ? <CircularProgress size={18} color="inherit" /> : undefined
          }
        >
          {loading ? "Adding..." : "Add User"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
